import { supabase } from '../config/supabaseConfig.ts';
import { checkAgentName } from '../utils/agentName';
import { proyectService, PROJECT_COLUMNS } from './proyectService';
import { getModuleConfigById } from '../utils/moduleConfigs';
import { getDefaultChannels } from '../utils/channelsConfig';

// Agentes de la plataforma propia (motor nuevo). Todo lo hace el admin por RLS y por las funciones de los scripts 22, 23, 27 y 30
// de smooth-stats-dashboard/supabase/security. La clave de OpenAI se guarda en Vault: el navegador la escribe y nunca la lee.

const AGENT_COLS = 'id, project_id, name, description, status, model, published_version, credential_id, monthly_cap_usd, is_primary, managed_by, external_url, external_note, created_at, updated_at';
// Script 46 (público/privado y clonar). Mientras no esté aplicado, esas columnas no existen: se piden sin ellas y las funciones de clonar avisan.
const COLS_46 = ', visibility, created_by, cloned_from';
let has46 = true;
// Script 49 (ambiente: demo, pruebas o producción): igual, se pide la columna solo si ya existe.
let has49 = true;
const agentCols = () => AGENT_COLS + (has46 ? COLS_46 : '') + (has49 ? ', environment' : '');
const noColumn = (error) => /column|does not exist|no existe/i.test(error?.message || '');
const missing46 = (error) => has46 && /visibility|cloned_from|created_by/i.test(error?.message || '') && noColumn(error);
const missing49 = (error) => has49 && /environment/i.test(error?.message || '') && noColumn(error);
/** Corre la consulta; si falla por una columna de un script que aún no se corrió, la quita de la lista y reintenta. */
async function withColumnFallback(fn) {
  for (let i = 0; i < 3; i += 1) {
    try { return await fn(); }
    catch (e) {
      if (missing46(e)) has46 = false;
      else if (missing49(e)) has49 = false;
      else throw e;
    }
  }
  return fn();
}
const PROJECT_COLS = 'id, name, engine, phone_number_id';

/** Errores de Postgres → mensajes que el admin entiende. */
export function friendlyError(error) {
  const msg = error?.message || String(error || '');
  if (/forbidden|42501|permission denied/i.test(msg)) return 'Tu usuario no tiene permiso para esto.';
  if (/agents_model_price|sin precio|no tiene precio/i.test(msg)) return 'Ese modelo no tiene precio en "Precios de modelos". Dalo de alta antes de usarlo.';
  if (/agents_credential_uq/i.test(msg)) return 'Esa clave es privada y ya la usa otro agente. Márcala como pública para compartirla.';
  if (/agents_environment_check/i.test(msg)) return 'El ambiente solo puede ser demo, pruebas o producción.';
  if (/agents_visibility_check/i.test(msg)) return 'La visibilidad solo puede ser pública o privada.';
  if (/agents_external_url_check/i.test(msg)) return 'La dirección debe empezar con http:// o https://.';
  return msg.replace(/^.*?ERROR:\s*/i, '') || 'Algo salió mal. Intenta de nuevo.';
}

// Módulo de Chats encendido, con los mismos permisos que trae por defecto el formulario de proyectos.
const CHATS_MODULE = {
  module: 'chats', view: true, actions: {},
  special_actions: { export: true, human_atention: true, export_chat: true, delete_chat: true, can_block_contact: false, create_chat: true, filters: true,
    can_view_budget_filter: false, can_view_follow_up_filter: false, can_view_notice_filter: false, can_view_generic_filter: false },
};
export function withChats(modules) {
  const list = Array.isArray(modules) ? modules.map((m) => ({ ...m })) : [];
  const chats = list.find((m) => m.module === 'chats');
  if (chats) chats.view = true; else list.unshift({ ...CHATS_MODULE, special_actions: { ...CHATS_MODULE.special_actions } });
  return list;
}

async function adminLog(action, details) {
  try {
    const { data } = await supabase.auth.getUser();
    const id = data?.user?.id;
    if (id) await supabase.from('admin_log').insert({ admin_id: id, target_user_id: id, action, details });
  } catch { /* la bitácora no debe tumbar la operación */ }
}

export const agentsService = {
  async listProjects() {
    const { data, error } = await supabase.from('project').select(PROJECT_COLS).order('name');
    if (error) throw error;
    return data || [];
  },

  async listProjectTypes() {
    const { data, error } = await supabase.from('project_types').select('id, name, default_modules').order('name');
    if (error) throw error;
    return data || [];
  },

  /** Proyecto mínimo desde la pantalla de agentes: mismo insert que el formulario de Proyectos, módulos del tipo y Chats encendido. */
  async createProjectQuick({ name, description, type }) {
    const project = await proyectService.createProyect({
      name: name.trim(),
      description: description.trim() || null,
      project_type: type.id,
      project_manager_id: null,
      platform: null,
      status: 'active',
      modules: withChats(type.default_modules || getModuleConfigById(type.id)),
      channels: getDefaultChannels(),
    });
    await adminLog('project_create_from_agents', { project_id: project.id });
    return project;
  },

  /** El proyecto completo, como lo espera el formulario de edición de Proyectos. */
  async getProjectForEdit(id) {
    const { data, error } = await supabase.from('project').select(PROJECT_COLUMNS).eq('id', id).single();
    if (error) throw error;
    return data;
  },

  async listModels() {
    // Los empleados no leen model_pricing: el script 47 les da la lista por RPC (si falta, se lee la tabla como antes).
    const viaRpc = await supabase.rpc('staff_models');
    if (!viaRpc.error && Array.isArray(viaRpc.data) && viaRpc.data.length) return viaRpc.data;
    const { data, error } = await supabase.from('model_pricing').select('model').is('valid_to', null).order('model');
    if (error) throw error;
    return [...new Set((data || []).map((r) => r.model))].filter((m) => !/embedding|whisper|transcri/i.test(m));
  },

  /** Lista para la pantalla de agentes: agente + proyecto + conexiones + credencial + borrador/publicada + gasto del mes. */
  async listAgents() {
    return withColumnFallback(() => this.listAgentsOnce());
  },

  async listAgentsOnce() {
    const [agents, conns, versions, creds, costs] = await Promise.all([
      supabase.from('agents').select(`${agentCols()}, project:project_id(${PROJECT_COLS})`).order('updated_at', { ascending: false }),
      supabase.from('channel_connections').select('id, agent_id, channel, status, external'),
      supabase.from('agent_versions').select('agent_id, version, state, prompt, config'),
      supabase.rpc('credential_list'),
      this.monthCosts().catch(() => ({})),
    ]);
    for (const r of [agents, conns, versions, creds]) if (r.error) throw r.error;
    const credById = Object.fromEntries((creds.data || []).map((c) => [c.id, c]));
    return (agents.data || []).map((a) => {
      const vs = (versions.data || []).filter((v) => v.agent_id === a.id);
      return {
        ...a,
        credential: a.credential_id ? credById[a.credential_id] || null : null,
        connections: (conns.data || []).filter((c) => c.agent_id === a.id),
        draft: vs.find((v) => v.state === 'draft') || null,
        published: vs.find((v) => v.state === 'published' && v.version === a.published_version) || null,
        monthCost: costs[a.id] ?? null,
      };
    });
  },

  async monthCosts() {
    const now = new Date();
    const mx = new Date(now.toLocaleString('en-US', { timeZone: 'America/Mexico_City' }));
    const from = `${mx.getFullYear()}-${String(mx.getMonth() + 1).padStart(2, '0')}-01`;
    const to = `${mx.getFullYear()}-${String(mx.getMonth() + 1).padStart(2, '0')}-${String(mx.getDate()).padStart(2, '0')}`;
    const { data, error } = await supabase.rpc('agent_costs', { p_from: from, p_to: to });
    if (error) throw error;
    return Object.fromEntries((data || []).map((r) => [r.agent_id, { usd: Number(r.cost_usd), messages: Number(r.messages), withoutCost: Number(r.messages_without_cost) }]));
  },

  async getAgent(id) {
    return withColumnFallback(() => this.getAgentOnce(id));
  },

  async getAgentOnce(id) {
    const [agent, versions, conns, creds] = await Promise.all([
      supabase.from('agents').select(`${agentCols()}, project:project_id(${PROJECT_COLS})`).eq('id', id).maybeSingle(),
      supabase.from('agent_versions').select('id, version, state, prompt, config, created_at, published_at').eq('agent_id', id).order('version', { ascending: false }),
      supabase.from('channel_connections').select('id, channel, status, external, public_key, last_event_at, last_error, created_at').eq('agent_id', id).order('id'),
      supabase.rpc('credential_list'),
    ]);
    for (const r of [agent, versions, conns, creds]) if (r.error) throw r.error;
    if (!agent.data) return null;
    const vs = versions.data || [];
    const allCreds = creds.data || [];
    return {
      agent: agent.data,
      project: agent.data.project,
      versions: vs,
      draft: vs.find((v) => v.state === 'draft') || null,
      published: vs.find((v) => v.state === 'published' && v.version === agent.data.published_version) || null,
      connections: conns.data || [],
      credential: allCreds.find((c) => c.id === agent.data.credential_id) || null,
      // Públicas: se pueden asignar a cualquier agente (pruebas de concepto). Libres: privadas guardadas que no usa ningún agente.
      publicCredentials: allCreds.filter((c) => c.is_public && c.status === 'active' && c.id !== agent.data.credential_id),
      freeCredentials: allCreds.filter((c) => !c.is_public && !c.agent_id && c.status === 'active'),
    };
  },

  async createAgent({ projectId, name, model, managedBy = 'platform', externalUrl = '', externalNote = '' }) {
    const row = { project_id: projectId, name: name.trim(), managed_by: managedBy };
    if (managedBy === 'platform') row.model = model;
    else { row.external_url = externalUrl.trim() || null; row.external_note = externalNote.trim() || null; }
    const { data, error } = await supabase.from('agents')
      .insert(row)
      .select('id').single();
    if (error) throw error;
    await adminLog('agent_create', { agent_id: data.id, project_id: projectId });
    return data.id;
  },

  /** Cambia el nombre de un agente (lo ven el equipo, la lista y el título del chat web). No toca nada más: id, versiones y canales siguen igual. */
  async renameAgent(id, rawName) {
    const { name, error: bad } = checkAgentName(rawName);
    if (bad) throw new Error(bad);
    const { data, error } = await supabase.from('agents').update({ name }).eq('id', id).select('id').maybeSingle();
    if (error) throw error;
    if (!data) throw new Error('No tienes permiso para cambiar este agente, o ya no existe.');
    await adminLog('agent_rename', { agent_id: id, name });
    return name;
  },

  /** Ambiente del agente: 'demo', 'test' o 'production'. Solo un admin lo pasa a producción o lo saca de ahí (la base de datos lo exige). */
  async setEnvironment(id, environment) {
    const { error } = await supabase.from('agents').update({ environment }).eq('id', id);
    if (error) throw error;
    await adminLog('agent_environment', { agent_id: id, environment });
  },

  /** Público = plantilla que cualquier admin puede clonar; privado = solo la clona quien lo creó. */
  async setVisibility(id, visibility) {
    const { error } = await supabase.from('agents').update({ visibility }).eq('id', id);
    if (error) throw error;
    await adminLog('agent_visibility', { agent_id: id, visibility });
  },

  /** Clona un agente (prompt, personalidad, herramientas y conocimiento propio) sin credenciales ni canales. Devuelve { agent_id, tools, knowledge }. */
  async cloneAgent({ sourceId, projectId, name, useDraft = false }) {
    const { data, error } = await supabase.rpc('agent_clone', { p_source: sourceId, p_project: projectId, p_name: name.trim(), p_use_draft: useDraft });
    if (error) throw (/agent_clone/i.test(error.message || '') && /could not find|schema cache|does not exist/i.test(error.message || '') ? new Error('Falta correr el script 46 en la base de datos.') : error);
    await adminLog('agent_clone', { source_id: sourceId, agent_id: data.agent_id, project_id: projectId });
    return data;
  },

  async updateAgent(id, patch) {
    const { error } = await supabase.from('agents').update(patch).eq('id', id);
    if (error) throw error;
  },

  /** Dónde vive el agente: 'platform' (se configura aquí) o externo ('n8n', 'web', 'other') con su dirección y nota. */
  async setManagedBy(id, { managedBy, externalUrl = '', externalNote = '' }) {
    const patch = managedBy === 'platform'
      ? { managed_by: 'platform' }
      : { managed_by: managedBy, external_url: externalUrl.trim() || null, external_note: externalNote.trim() || null };
    const { error } = await supabase.from('agents').update(patch).eq('id', id);
    if (error) throw error;
    await adminLog('agent_managed_by', { agent_id: id, managed_by: managedBy });
  },

  async saveDraft(draftId, { prompt, config }) {
    const patch = {};
    if (prompt !== undefined) patch.prompt = prompt;
    if (config !== undefined) patch.config = config;
    const { error } = await supabase.from('agent_versions').update(patch).eq('id', draftId).eq('state', 'draft');
    if (error) throw error;
  },

  async publish(agentId) {
    const { data, error } = await supabase.rpc('agent_publish', { p_agent: agentId });
    if (error) throw error;
    await adminLog('agent_publish', { agent_id: agentId, result: data });
    return data;
  },

  async archive(agentId) {
    const { error } = await supabase.rpc('agent_set_status', { p_agent: agentId, p_status: 'archived' });
    if (error) throw error;
    await adminLog('agent_archive', { agent_id: agentId });
  },

  async restore(agentId) {
    const { error } = await supabase.rpc('agent_set_status', { p_agent: agentId, p_status: 'draft' });
    if (error) throw error;
    await adminLog('agent_restore', { agent_id: agentId });
  },

  /**
   * Guarda una clave nueva en Vault (privada, o pública si `isPublic`) y la asigna al agente. Si la anterior era privada y quedó sin uso,
   * la borra (no deja secretos huérfanos); una pública NUNCA se borra aquí: la pueden estar usando otros agentes.
   */
  async replaceCredential(agentId, { label, secret, isPublic = false }, previous) {
    const { data, error } = await supabase.rpc('credential_save', { p_label: label.trim(), p_secret: secret.trim() });
    if (error) throw error;
    const newId = data?.id;
    if (isPublic) {
      const pub = await supabase.rpc('credential_update', { p_id: newId, p_public: true });
      if (pub.error) throw new Error(`La clave se guardó, pero no se pudo hacer pública: ${friendlyError(pub.error)}`);
    }
    await this.updateAgent(agentId, { credential_id: newId });
    if (previous?.id && !previous.is_public) await supabase.rpc('credential_delete', { p_id: previous.id });   // si otro agente la usa, la función se niega y no pasa nada
    await adminLog('agent_credential', { agent_id: agentId, credential_id: newId, last4: data?.last4, is_public: Boolean(isPublic) });
    return data;
  },

  /** Claves públicas activas (para elegirlas al crear un agente o cambiar la de uno). */
  async listPublicCredentials() {
    const { data, error } = await supabase.rpc('credential_list');
    if (error) throw error;
    return (data || []).filter((c) => c.is_public && c.status === 'active');
  },

  /**
   * Edita una credencial: nombre, clave nueva (cuando caduca o cambia; se reemplaza en su lugar para todos los agentes que la usan) y si es
   * pública. Solo se mandan los campos que cambiaron. La clave nunca se vuelve a leer ni se guarda en la bitácora.
   */
  async updateCredential(id, { label, secret, isPublic }) {
    const args = { p_id: id };
    if (label !== undefined) args.p_label = label.trim();
    if (secret) args.p_secret = secret.trim();
    if (isPublic !== undefined) args.p_public = isPublic;
    const { data, error } = await supabase.rpc('credential_update', args);
    if (error) throw error;
    await adminLog('credential_update', { credential_id: id, renamed: label !== undefined, key_replaced: Boolean(secret), is_public: isPublic, last4: secret ? data?.last4 : undefined });
    return data;
  },

  async useCredential(agentId, credentialId) {
    await this.updateAgent(agentId, { credential_id: credentialId });
    await adminLog('agent_credential', { agent_id: agentId, credential_id: credentialId });
  },

  async setEngine(projectId, engine) {
    const { data, error } = await supabase.rpc('project_set_engine', { p_project: projectId, p_engine: engine });
    if (error) throw error;
    await adminLog('project_engine', { project_id: projectId, engine });
    return data;
  },

  // ── Actividad (trazas del motor, script 36). Si el script no se ha corrido, avisa con un código propio en vez de un error de SQL. ──
  async traceSettings(agentId) {
    const { data, error } = await supabase.from('agents').select('trace_retention_days').eq('id', agentId).maybeSingle();
    if (error) { if (/trace_retention_days|agent_traces|does not exist|schema cache/i.test(error.message)) { const e = new Error('Falta correr el script 36 (actividad del agente) en Supabase.'); e.code = 'missing_36'; throw e; } throw error; }
    return { retentionDays: data?.trace_retention_days ?? 7 };
  },

  async setTraceRetention(agentId, days) {
    const { error } = await supabase.from('agents').update({ trace_retention_days: days }).eq('id', agentId);
    if (error) throw error;
    await adminLog('agent_trace_retention', { agent_id: agentId, days });
  },

  /** Lista sin los pasos (pesan); `before` es el id de la última traza vista, para ir hacia atrás. `channel` y `since` (ISO) filtran en el servidor. */
  async listTraces(agentId, { status, channel, since, before, limit = 40 } = {}) {
    let q = supabase.from('agent_traces')
      .select('id, created_at, channel, chat_id, status, reason, input, output, model, prompt_tokens, completion_tokens, cost_usd, latency_ms, attempt, agent_version')
      .eq('agent_id', agentId).order('id', { ascending: false }).limit(limit);
    if (status) q = q.eq('status', status);
    if (channel) q = q.eq('channel', channel);
    if (since) q = q.gte('created_at', since);
    if (before) q = q.lt('id', before);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  },

  /**
   * Cuántas interacciones hay por resultado y canal desde `since` (script 44): [{ status, channel, n }]. Sin el script devuelve null y la
   * pantalla simplemente no muestra contadores.
   */
  async traceCounts(agentId, since) {
    const { data, error } = await supabase.rpc('agent_trace_counts', { p_agent: agentId, ...(since ? { p_since: since } : {}) });
    if (error) { if (/agent_trace_counts|schema cache|Could not find|does not exist/i.test(error.message || '')) return null; throw error; }
    return data || [];
  },

  async getTrace(id) {
    const { data, error } = await supabase.from('agent_traces').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return data;
  },
};
